/* eslint no-shadow: ["error", { "allow": ["state", "getters"] }] */
import Vue from 'vue'
import { findByKey, setProp } from 'vuex-intern'
import { geohashForLocation } from 'geofire-common'
import {
  firebase,
  firebaseAuth,
  firebaseStorage,
  db,
  usersCollection,
  slugsCollection,
} from '../../../firebase'

const state = {
  campaign: null,
  campaigns: null,
  record: null,
  records: [],
  recordsCount: 0,
  totalRecordCounts: 0,
  dailyRecordCounts: 0,
  monthlyRecordCounts: 0,
  campaignSize: 0,
}

const isEmpty = obj => [Object, Array].includes((obj || {}).constructor) && !Object.entries((obj || {})).length

const keysList = {
  campaign: [
    'address',
    'formattedAddress',
    'zip',
    'country',
    'contactNumber',
    'campaignName',
    'types',
    'geoLocation',
    'geohash',
    'slug',
    'cuid',
    'cid',
    'zip',
    'status',
    'countryCode',
    'totalRecords',
    'socialMedia',
  ],
  record: [
    'uid',
    'user',
    'cid',
    'cuid',
    'slug',
    'campaign',
    'geoLocation',
    'geohash',
    'createAt',
  ],
}

const getters = {
  campaign: state => state.campaign,
  campaignBySlug: findByKey('campaigns', 'slug'),
  campaigns: state => state.campaigns,
  record: state => state.record,
  records: state => state.records,
  recordsCount: state => state.recordsCount,
  totalRecordCounts: state => state.totalRecordCounts,
  dailyRecordCounts: state => state.dailyRecordCounts,
  monthlyRecordCounts: state => state.monthlyRecordCounts,
}

const mutations = {
  recordCounts: (state, { total, daily, monthly }) => {
    if (total) {
      // yes
    } else if (daily) {
      state.dailyRecordCounts = daily
    } else if (monthly) {
      state.monthlyRecordCounts = monthly
    }
  },
  setCampaignSize: (state, size) => {
    state.campaignSize = size
  },
  setCampaignRecords: (state, records) => {
    if (records && records.constructor === Array) {
      state.records = records
      state.recordsCount = records.length
    }
  },
  setCampaigns: (state, campaigns) => {
    state.campaigns = campaigns
  },
  updateCampaignsFields: (state, { cid, fields }) => {
    const objIndex = state.campaigns.findIndex((obj => obj.cid === cid))
    if (fields.status === 'deleted') {
      state.campaigns.splice(objIndex, 1)
    } else {
      Object.entries(fields).forEach(([key, value]) => {
        state.campaigns[objIndex][key] = value
      })
    }
  },
  setCampaign: setProp('campaign'),
  setCampaignMutation: (state, { keyName, object, reset }) => {
    if (keyName && object === null) {
      state[keyName] = null
    } else if (keyName && object) {
      const keys = keysList[keyName]
      const obj = {}

      if (!reset && (state[keyName] === 'undefined' || state[keyName] === null)) {
        state[keyName] = {}
      }

      keys.forEach(key => {
        if (Object.prototype.hasOwnProperty.call(object, key)) {
          if (object[key] !== 'undefined' && object[key] !== null) {
            if (reset) {
              obj[key] = object[key]
            } else {
              state[keyName][key] = object[key]
            }
          }
        }
      })

      if (reset) {
        state[keyName] = obj
      }
    }
  },
}

const actions = {
  getDailyRecordCounts: async ({ commit }, { date, uid }) => {
    // date format YYYY-MM-DD
    if (date && uid) {
      const start = new Date(date)
      const end = new Date(date)
      end.setDate(end.getDate() + 1)

      const totalRecords = db.collectionGroup('totalRecords')
        .where('uid', '==', uid)
        .where('updateAt', '>=', start)
        .where('updateAt', '<', end)
        .orderBy('updateAt', 'desc')

      await totalRecords.get().then(async snapshot => {
        if (snapshot.empty) {
          // do something
        } else {
          let recordCounts = 0
          await snapshot.forEach(doc => {
            recordCounts += doc.data().recordCounts
          })
          commit('recordCounts', { daily: recordCounts })
        }
      })
    }
  },
  getMonthlyRecordCounts: async ({ commit }, { date, uid }) => {
    // TODO: Need a cron
    // date format YYYY-MM-DD
    if (date && uid) {
      const d = new Date(date)
      const start = new Date(d.getFullYear(), d.getMonth(), 1)
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 1)
      end.setDate(end.getDate() + 1)

      const totalRecords = db.collectionGroup('totalRecords')
        .where('uid', '==', uid)
        .where('updateAt', '>=', start)
        .where('updateAt', '<', end)
        .orderBy('updateAt', 'desc')

      await totalRecords.get().then(async snapshot => {
        if (snapshot.empty) {
          // do something
        } else {
          let recordCounts = 0
          await snapshot.forEach(doc => {
            recordCounts += doc.data().recordCounts
          })
          commit('recordCounts', { monthly: recordCounts })
        }
      })
    }
  },
  createCampaignRecord: async ({ commit, dispatch }, params) => {
    const record = {
      uid: params.uid,
      user: params.user,
      cid: params.cid,
      cuid: params.campaign.cuid,
      campaign: params.campaign,
      geoLocation: new firebase.firestore.GeoPoint(params.geoLocation.lat, params.geoLocation.lng),
      slug: params.slug.toLowerCase(),
    }

    // Create Customer to the account
    dispatch('addCampaignCustomer', record)

    await dispatch('getGeoHash', params.geoLocation).then(async geohash => {
      const time = {
        createAt: firebase.firestore.FieldValue.serverTimestamp(),
        updateAt: firebase.firestore.FieldValue.serverTimestamp(),
      }
      const campaignRef = usersCollection.doc(record.campaign.cuid).collection('campaigns').doc(record.campaign.cid)
      await campaignRef.collection('records')
        .add({ ...record, geohash, ...time })
        .then(() => {
          // Add record counts
          dispatch('incrementCounter', {
            ref: campaignRef,
            doc: 'totalRecords',
            col: 'recordCounts',
            uid: record.campaign.cuid,
          })
          // Update user tags
          dispatch('updateUserTags', { uid: record.uid, types: record.campaign.types })
          commit('setCampaignMutation', { keyName: 'record', object: { ...record, geohash, ...time } })
        })
    })
  },
  fetchUserRecord: async ({ commit }, { route, uid }) => {
    if (!isEmpty(route) && route.slug) {
      // Record in a Hour
      const now = Date.now()
      const expiresAt = new Date(now - 60 * 60 * 1000)

      if (route.slug) {
        // using slug
        const slug = route.slug.toLowerCase()
        const records = db.collectionGroup('records')
          .where('slug', '==', slug)
          .where('uid', '==', uid)
          .where('createAt', '>', expiresAt)
          .limit(1)
          .orderBy('createAt', 'desc')

        await records.get().then(async snapshot => {
          if (snapshot.empty) {
            commit('setCampaignMutation', { keyName: 'record', object: null })
          } else {
            const doc = snapshot.docs[0]
            commit('setCampaignMutation', { keyName: 'record', object: doc.data() })
          }
        })
      }
    }
  },
  fetchCampaignRecords: async ({ commit }, {
    cuid, cid, dates,
  }) => {
    let fetchRecords = []
    if (!isEmpty(cuid) && !isEmpty(cid)) {
      const fileName = `${cid} ${dates.start}`
      if (!isEmpty(localStorage.getItem(fileName))) {
        fetchRecords = JSON.parse(localStorage.getItem(fileName))
        commit('setCampaignRecords', fetchRecords)
      } else {
        let records = usersCollection.doc(cuid).collection('campaigns').doc(cid).collection('records')

        if (dates) {
          if (dates.start) {
            records = records.where('createAt', '>=', dates.start)
          }

          if (dates.end) {
            records = records.where('createAt', '<', dates.end)
          }
        }

        records = records.orderBy('createAt', 'desc')

        await records.get().then(snapshot => {
          if (snapshot.empty) {
            commit('setCampaignRecords', [])
          } else {
            snapshot.forEach(doc => {
              const data = doc.data()
              data.createAt = data.createAt.toDate()
              fetchRecords.push(data)
            })

            commit('setCampaignRecords', fetchRecords)
            const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 0)
            if (dates.start < today) {
              localStorage.setItem(fileName, JSON.stringify(fetchRecords))
            }
          }
        })
      }
    }
    return fetchRecords
  },
  fetchCampaigns: async ({ commit }, { uid }) => new Promise((resolve, reject) => {
    if (uid) {
      usersCollection.doc(uid).collection('campaigns')
        .where('status', 'in', ['eligible', 'paused', 'ended', 'pending', 'suspended'])
        .get()
        .then(snapshot => {
          commit('setCampaignSize', snapshot.size)
          if (snapshot.empty) {
            commit('setCampaigns', null)
            resolve(null)
          } else {
            const campaigns = []
            snapshot.forEach(doc => {
              campaigns.push({ ...doc.data(), cid: doc.id })
            })

            commit('setCampaigns', campaigns)
            resolve(campaigns)
          }
        })
        .catch(error => {
          reject(error)
        })
    } else {
      reject(new Error('Missing params'))
    }
  }),
  isSlugExist: (req, slug) => new Promise((resolve, reject) => {
    slugsCollection.doc(slug.toLowerCase()).get().then(snapshot => {
      if (snapshot.exists) {
        resolve(true)
      } else {
        resolve(false)
      }
    }).catch(error => {
      reject(error)
    })
  }),
  getCampaign: async ({ commit }, {
    cuid, cid, slug,
  }) => new Promise((resolve, reject) => {
    if (slug) {
      db.collectionGroup('campaigns')
        .where('slug', '==', slug.toLowerCase())
        .where('status', 'in', ['eligible', 'paused', 'ended', 'pending', 'suspended'])
        .limit(1)
        .get()
        .then(async snapshot => {
          if (!snapshot.empty) {
            const campaign = { ...snapshot.docs[0].data(), cid: snapshot.docs[0].id }
            if (campaign.cuid !== cuid) {
              commit('setCampaign')
              commit('setCampaignMutation', { keyName: 'campaign', object: null })
              resolve(null)
            } else {
              commit('setCampaign', campaign)
              // commit('setCampaignMutation', { keyName: 'campaign', object: campaign })
              resolve(campaign)
            }
          } else {
            commit('setCampaignMutation', { keyName: 'campaign', object: null })
            resolve(null)
          }
        })
    } else if (cuid && cid) {
      usersCollection.doc(cuid).collection('campaigns').doc(cid).get()
        .then(snapshot => {
          if (snapshot.exists) {
            commit('setCampaignMutation', { keyName: 'campaign', object: snapshot.data() })
            resolve(snapshot.data())
          } else {
            commit('setCampaignMutation', { keyName: 'campaign', object: null })
            resolve(null)
          }
        })
    } else {
      reject()
    }
  }),
  updateCampaign: ({ commit, dispatch }, {
    campaign, data, userCampaigns, uid,
  }) => {
    if (userCampaigns && data && campaign && uid && campaign.cid && campaign.slug) {
      const updateData = {}
      Object.entries(data).forEach(([key, value]) => {
        if (keysList.campaign.includes(key)) {
          updateData[key] = value
        }
      })
      const batch = db.batch()
      const newUserRef = usersCollection.doc(uid).collection('campaigns').doc(campaign.cid)
      batch.update(newUserRef, { ...updateData })

      if (updateData.campaignName) {
        const newCampaigns = userCampaigns.map(obj => (obj.cid === campaign.cid ? { ...obj, campaignName: updateData.campaignName } : obj))
        const userRef = usersCollection.doc(uid)
        batch.update(userRef, {
          campaigns: newCampaigns,
        })
        commit('setUserCampaigns', { campaigns: newCampaigns })
      }

      /* const campRef = slugsCollection.doc(campaign.slug.toLowerCase())
      batch.update(campRef, { ...updateData }) */
      batch.commit()
      const today = Vue.moment().format('YYYY-MM-DD')
      localStorage.removeItem(today + uid)
      dispatch('getCampaign', { slug: campaign.slug, cuid: uid })
    }
  },
  updateCampaignSocial: ({ commit }, { cuid, campaign, data }) => new Promise((resolve, reject) => {
    if (cuid && campaign && data) {
      const update = {}
      const newCampaign = {}
      Object.entries(data).forEach(([key, value]) => {
        if (value === null || value === '') {
          update[key] = null
        } else {
          update[key] = value
        }
        newCampaign[key] = update[key]
      })
      usersCollection.doc(cuid).collection('campaigns').doc(campaign.cid)
        .update({
          socialMedia: update,
        })
        .then(() => {
          commit('setCampaignMutation', { keyName: 'campaign', object: newCampaign })
          resolve()
        })
        .catch(err => {
          reject(err)
        })
    } else {
      reject()
    }
  }),
  updateCampaignStatus: ({ commit }, {
    cuid, status, campaign, userCampaigns,
  }) => {
    const campaignStatus = ['eligible', 'paused', 'deleted', 'ended', 'pending', 'suspended']
    if (cuid && campaign.cid && campaign.slug && campaignStatus.includes(status)) {
      const batch = db.batch()
      const newUserRef = usersCollection.doc(cuid).collection('campaigns').doc(campaign.cid)
      batch.update(newUserRef, { status })

      let newCampaigns = userCampaigns.map(obj => (obj.cid === campaign.cid ? { ...obj, status } : obj))

      if (status === 'deleted') {
        newCampaigns = userCampaigns.filter(obj => obj.cid !== campaign.cid)
      }

      /* const campRef = slugsCollection.doc(campaign.slug.toLowerCase())
      batch.update(campRef, { status }) */
      const userRef = usersCollection.doc(cuid)
      batch.update(userRef, {
        campaigns: newCampaigns,
      })

      batch.commit()
      commit('updateCampaignsFields', { cid: campaign.cid, fields: { status } })
      /* const today = Vue.moment().format('YYYY-MM-DD')
      localStorage.removeItem(today + cuid)
      commit('setUserCampaigns', { campaigns: newCampaigns }) */
    }
  },
  createCampaign: ({ commit, rootState, dispatch }, info) => new Promise((resolve, reject) => {
    // campaign status: Eligible, Paused, Deleted, Ended, Pending, Suspended
    const time = {
      createAt: firebase.firestore.FieldValue.serverTimestamp(),
      updateAt: firebase.firestore.FieldValue.serverTimestamp(),
    }

    dispatch('getGeoHash', info.geoLocation).then(geohash => {
      const cuid = (rootState.users.user.uid) ? rootState.users.user.uid : firebaseAuth.currentUser.uid

      const campaign = {
        cuid,
        address: info.address,
        formattedAddress: info.formattedAddress,
        zip: info.zip,
        country: info.country,
        contactNumber: info.contactNumber,
        campaignName: info.campaignName,
        types: info.types,
        geoLocation: new firebase.firestore.GeoPoint(info.geoLocation.lat, info.geoLocation.lng),
        slug: info.slug.toLowerCase(),
        paymentType: info.paymentType,
        status: 'eligible',
        countryCode: info.countryCode,
      }

      const batch = db.batch()

      const newUserRef = usersCollection.doc(campaign.cuid).collection('campaigns').doc()
      batch.set(newUserRef, { ...campaign, ...time, geohash })

      // inorder to allower user to update slug, we using another table to keep the slugs
      const campRef = slugsCollection.doc(campaign.slug)
      batch.set(campRef, { cid: newUserRef.id, ...campaign, ...time })

      const userRef = usersCollection.doc(campaign.cuid)
      batch.update(userRef, {
        campaigns: firebase.firestore.FieldValue.arrayUnion({
          cid: newUserRef.id, slug: campaign.slug, campaignName: campaign.campaignName, status: campaign.status, types: campaign.types,
        }),
      })

      batch.commit().then(() => {
        const today = Vue.moment().format('YYYY-MM-DD')
        localStorage.removeItem(today + cuid)
        dispatch('saveCampaignCountryCode', campaign.countryCode)
        commit('setCampaignMutation', { keyName: 'campaign', object: campaign })
        commit('setUserCampaigns', {
          campaign: {
            cid: newUserRef.id,
            slug: campaign.slug,
            campaignName: campaign.campaignName,
            types: campaign.types,
            status: campaign.status,
          },
        })
        resolve({ ...campaign, cid: newUserRef.id })
      }).catch(() => {
        const error = {
          code: 'slugUsed',
          message: 'Slug is used',
        }
        reject(error)
      })
    })
  }),
  geoAddress: async ({ commit, getters }, address) => new Promise((resolve, reject) => {
    Vue.$gmapApiPromiseLazy().then(() => {
      // eslint-disable-next-line no-undef
      const service = new google.maps.Geocoder({ language: 'zh-TW' })
      service.geocode({ address }, (results, status) => {
        if (status === 'OK') {
          const info = {
            formattedAddress: results[0].formatted_address,
            geoLocation: {
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            },
          }

          commit('setCampaignMutation', { keyName: 'campaign', object: info })
          resolve(getters.campaign)
        } else {
          reject(status)
        }
      })
    })
  }),
  getGeoHash: async (res, params) => new Promise(resolve => {
    resolve(geohashForLocation([params.lat, params.lng]))
  }),
  uploadCampaignImage: async (ctx, {
    data, name, uid, type, cid, colName,
  }) => new Promise((resolve, reject) => {
    if (type === 'data_url') {
      firebaseStorage.ref('users').child(`${uid}/${name}.png`).putString(data, type).then(() => {
        firebaseStorage.ref('users').child(`${uid}/${name}.png`).getDownloadURL().then(url => {
          usersCollection.doc(uid).collection('campaigns').doc(cid).update({ [colName]: url })
            .then(() => {
              resolve()
            })
            .catch(err => {
              reject(err)
            })
        })
          .then(() => {
            resolve()
          })
      })
    } else if (type === 'blob') {
      firebaseStorage.ref('users').child(`${uid}/${name}.png`).put(data).then(() => {
        firebaseStorage.ref('users').child(`${uid}/${name}.png`).getDownloadURL().then(url => {
          usersCollection.doc(uid).collection('campaigns').doc(cid).update({ [colName]: url })
            .then(() => {
              resolve()
            })
            .catch(err => {
              reject(err)
            })
        })
          .catch(err => {
            reject(err)
          })
      })
    }
  }),
  saveCampaignCountryCode: async (ctx, code) => {
    db.collection('system').doc('countries').set({
      campaignCountryCodes: firebase.firestore.FieldValue.arrayUnion(code),
    }, { merge: true })
  },
}

export default {
  state,
  mutations,
  getters,
  actions,
}
